/**
 * Workspace (glossary; plan §5b): a chatter works chats from many Accounts in
 * one tab. Widgets on the left (Search, Pinned, Activity, Feed, Active chats)
 * bring chats to them; the chat opens on the right in the React
 * chat (ADR 0021). Layout engine ported from the design's Workspace.dc.html.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState, type MouseEvent as ReactMouseEvent, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FeatherIcon } from '../components/FeatherIcon';
import { LoadingBlock, Spinner } from '../components/Spinner';
import { useAuth } from '../context/AuthContext';
import { ClaimBottomBar } from '../context/SidebarContext';
import { useCrmHost } from '../crm/CrmHostContext';
import type { CrmAccount } from '../crm/crmAccess';
import { activityItems, activitySettingsFrom, type ActivityItem, type ActivitySettings, type WorkspaceEvent } from '../workspace/activity';
import { fetchFanInfo, type FanInfo, type PinRow, type SearchHit } from '../workspace/api';
import { pinStore, usePins } from '../workspace/pinStore';
import { addableWidgets, boardReducer, initialBoard, leftZoneWidth, parseSavedBoard, savedPart, type BoardAction, type ListWidgetId } from '../workspace/board';
import { activeCards, activityCard, feedCards, pinCards, type CardContext } from '../workspace/cards';
import type { ChatStateRow } from '../workspace/chats';
import { feedSettingsFrom, type FeedItem, type FeedSettings } from '../workspace/feed';
import { useChatDrag, type DragChat, type DragSource, type DropTarget } from '../workspace/useChatDrag';
import { useFeed } from '../workspace/useFeed';
import { FeedSettingsPanel } from '../workspace/widgets/FeedSettings';
import { ACTIVE_WINDOWS, activeChatGroups, chatKey, type ActiveWindow } from '../workspace/chats';
import { useDismissed } from '../workspace/dismissed';
import { useActiveChats } from '../workspace/useActiveChats';
import { ChatPane, type OpenChat } from '../workspace/ChatPane';
import { COLOUR_MODES, accountColour, colourMode as readColourMode } from '../workspace/colour';
import { WIDGET_IDS, cellRect, geometry, type WidgetId } from '../workspace/grid';
import { useStableArray, useStableCallback } from '../workspace/useStable';
import { useWorkspace, useWorkspaceLive } from '../workspace/WorkspaceProvider';
import { ActivityFilters } from '../workspace/widgets/ActivityFilters';
import { ChatCollection, type CardItem } from '../workspace/widgets/ChatCollection';
import { SearchWidget } from '../workspace/widgets/SearchWidget';
import type { FrameState } from '../workspace/widgets/frame';
import { WidgetFrame } from '../workspace/widgets/WidgetFrame';
import { Avatar, FanAvatar } from '../workspace/widgets/Avatar';
import { Dropdown } from '../workspace/widgets/Dropdown';
import { TabsView, type TabsDrag } from '../workspace/TabsView';
import { PHONE_QUERY, readLastTab, saveLastTab, tabsZoneWidth, viewOf, type WorkspaceTab, type WorkspaceView } from '../workspace/workspaceView';
import { useMediaMatch } from '../hooks/useIsNarrow';
import '../chats/chats.css';
import '../workspace/workspace.css';
import { inApp, uiScale } from '../theme/uiScale';

const WIDGET_ICON: Record<WidgetId, string> = { search: 'search', activity: 'activity', feed: 'sparkles', active: 'messages-square', pinned: 'pin' };
const LIVE_KINDS = new Set(['fan_message', 'tip', 'ppv_bought', 'team_sent', 'ai_sent']);
/** Activity draws at most this many rows (the count still shows them all). */
const ACTIVITY_MAX_ROWS = 150;
const BLIP_GAP_MS = 2000;

let audio: AudioContext | null = null;
let lastBlip = 0;
/** A short soft blip for new Activity (no asset to load); one shared player, at most one per BLIP_GAP_MS. */
/** Where a floating panel of `width` opens under its button, in the app's own (zoomed) units. */
function floatingAt(r: DOMRect, width: number): { top: number; left: number } {
  const w = width * uiScale();
  return { top: inApp(r.bottom + 6), left: inApp(Math.max(12, Math.min(r.right - w, window.innerWidth - w - 12))) };
}

function blip() {
  const at = Date.now();
  if (at - lastBlip < BLIP_GAP_MS) return;
  lastBlip = at;
  try {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    audio ??= new Ctx();
    const ctx = audio;
    if (ctx.state === 'suspended') void ctx.resume();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.08, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.26);
  } catch {
    /* audio unavailable */
  }
}

type Menu = null | 'layout' | 'add' | 'scope' | 'filters' | 'feedSettings';

export function WorkspacePage() {
  const { t } = useTranslation('workspace');
  const { t: tc } = useTranslation('chat');
  const crm = useCrmHost();
  const { user } = useAuth();
  const ws = useWorkspace();
  const live = useWorkspaceLive();
  const accountsById = useMemo(() => new Map(ws.accounts.map((a) => [a.account_id, a])), [ws.accounts]);

  // Workspace is a CRM-view destination.
  useEffect(() => {
    if (crm.mode !== 'crm' && crm.access.state === 'ok') crm.switchMode('crm');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crm.access.state]);

  // ── view: tabs (default) or canvas ───────────────────────────────────────
  const phone = useMediaMatch(PHONE_QUERY);
  const view: WorkspaceView = viewOf(ws.settings.view, phone);
  const [tab, setTabState] = useState<WorkspaceTab>(readLastTab);
  const setTab = useCallback((next: WorkspaceTab) => {
    setTabState(next);
    saveLastTab(next);
  }, []);
  const [searchRequest, setSearchRequest] = useState(0);
  // Phones show the list or the open chat, never both.
  const [phoneChat, setPhoneChat] = useState(false);
  const toList = useCallback(() => setPhoneChat(false), []);

  // ── board ────────────────────────────────────────────────────────────────
  const [board, rawDispatch] = useReducer(boardReducer, undefined, initialBoard);
  const loadedBoard = useRef(false);
  const persistBoard = useRef(false);
  const dispatch = useCallback((action: BoardAction, persist = true) => {
    if (persist) persistBoard.current = true;
    rawDispatch(action);
  }, []);
  useEffect(() => {
    if (!ws.prefsLoaded || loadedBoard.current) return;
    loadedBoard.current = true;
    const saved = parseSavedBoard(ws.layout);
    if (saved) rawDispatch({ type: 'load', saved });
  }, [ws.prefsLoaded, ws.layout]);
  useEffect(() => {
    if (!persistBoard.current || board.editing || !loadedBoard.current) return;
    persistBoard.current = false;
    ws.saveLayout(savedPart(board) as unknown as Record<string, unknown>);
    // ws.saveLayout is stable; only board changes matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board]);

  // ── measuring the zone ───────────────────────────────────────────────────
  const rowRef = useRef<HTMLDivElement>(null);
  const zoneRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ zw: 880, zh: 900, rw: 1600, measured: false });
  useEffect(() => {
    // The widget zone exists only on the canvas; the row (for the tabs list width) always.
    const zone = zoneRef.current;
    const row = rowRef.current;
    if (!row) return;
    const measure = () =>
      setSize((s) => {
        const next = zone ? { zw: zone.clientWidth, zh: zone.clientHeight, rw: row.clientWidth, measured: true } : { ...s, rw: row.clientWidth };
        return s.zw === next.zw && s.zh === next.zh && s.rw === next.rw && s.measured === next.measured ? s : next;
      });
    // Measure now too: observer callbacks wait for a rendering step, which a hidden tab never takes.
    measure();
    const ro = new ResizeObserver(measure);
    if (zone) ro.observe(zone);
    ro.observe(row);
    return () => ro.disconnect();
  }, [ws.enabled, view]);
  const geo = useMemo(() => geometry(size.zw, size.zh), [size.zw, size.zh]);
  useEffect(() => {
    if (size.measured && view === 'canvas') dispatch({ type: 'fit', geometry: geo }, false);
  }, [geo, size.measured, dispatch, view]);
  const [animate, setAnimate] = useState(false);
  useEffect(() => {
    if (!size.measured) return;
    const timer = window.setTimeout(() => setAnimate(true), 60);
    return () => window.clearTimeout(timer);
  }, [size.measured]);

  // ── move / resize / divider ──────────────────────────────────────────────
  const [drag, setDrag] = useState<{ id: WidgetId; dx: number; dy: number; orig: { x: number; y: number; w: number; h: number } } | null>(null);
  const [resizing, setResizing] = useState<WidgetId | null>(null);
  const [dividerDrag, setDividerDrag] = useState<number | null>(null);
  const boardRef = useRef(board);
  boardRef.current = board;

  const follow = (onMove: (e: globalThis.PointerEvent) => void, onUp: () => void) => {
    const up = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', up);
      onUp();
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', up);
  };

  const startMove = (id: WidgetId) => (e: PointerEvent) => {
    if (!boardRef.current.editing || e.button !== 0 || (e.target as HTMLElement).closest('button,input')) return;
    e.preventDefault();
    const orig = { ...boardRef.current.layout[id] };
    const sx = e.clientX;
    const sy = e.clientY;
    let last = '';
    setDrag({ id, dx: 0, dy: 0, orig });
    follow(
      (ev) => {
        const dx = ev.clientX - sx;
        const dy = ev.clientY - sy;
        const x = orig.x + Math.round(dx / (geo.colW + geo.gap));
        const y = orig.y + Math.round(dy / (geo.rowH + geo.gap));
        const k = `${x}:${y}`;
        if (k !== last) {
          last = k;
          dispatch({ type: 'move', id, to: { x, y } });
        }
        setDrag({ id, dx, dy, orig });
      },
      () => setDrag(null),
    );
  };

  const startResize = (id: WidgetId) => (e: PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const orig = { ...boardRef.current.layout[id] };
    const sx = e.clientX;
    const sy = e.clientY;
    setResizing(id);
    follow(
      (ev) =>
        dispatch({
          type: 'resize',
          id,
          size: { w: orig.w + Math.round((ev.clientX - sx) / (geo.colW + geo.gap)), h: orig.h + Math.round((ev.clientY - sy) / (geo.rowH + geo.gap)) },
        }),
      () => setResizing(null),
    );
  };

  const leftW = leftZoneWidth(dividerDrag ?? board.leftW, size.rw);
  // The zone's own width follows leftW: read it back right away instead of waiting for the observer.
  useLayoutEffect(() => {
    const zone = zoneRef.current;
    if (!zone) return;
    setSize((s) => (s.zw === zone.clientWidth && s.zh === zone.clientHeight ? s : { ...s, zw: zone.clientWidth, zh: zone.clientHeight }));
  }, [leftW]);
  // The tabs list has its own width (it starts at the Chats page's), kept in the person's settings.
  const tabsW = tabsZoneWidth(view === 'tabs' && dividerDrag != null ? dividerDrag : ws.settings.tabsWidth, size.rw);
  const startDivider = (e: PointerEvent) => {
    e.preventDefault();
    const sx = e.clientX;
    const tabs = view === 'tabs';
    const w0 = tabs ? tabsZoneWidth(ws.settings.tabsWidth, size.rw) : leftZoneWidth(boardRef.current.leftW, size.rw);
    let w = w0;
    setDividerDrag(w0);
    follow(
      (ev) => {
        w = w0 + ev.clientX - sx;
        setDividerDrag(w);
      },
      () => {
        setDividerDrag(null);
        if (tabs) ws.updateSettings({ tabsWidth: tabsZoneWidth(w, size.rw) });
        else dispatch({ type: 'setLeftW', width: leftZoneWidth(w, size.rw) });
      },
    );
  };

  // ── open chat + history ──────────────────────────────────────────────────
  const [history, setHistory] = useState<{ list: OpenChat[]; pos: number }>({ list: [], pos: -1 });
  const open = history.list[history.pos] ?? null;
  const openKey = open ? chatKey(open.accountId, open.fanId) : null;
  const openChat = useCallback((c: OpenChat) => {
    setPhoneChat(true);
    setHistory((h) => {
      const cur = h.list[h.pos];
      if (cur && cur.accountId === c.accountId && cur.fanId === c.fanId) return h;
      const list = [...h.list.slice(0, h.pos + 1), c].slice(-50);
      return { list, pos: list.length - 1 };
    });
  }, []);
  const back = useCallback(() => setHistory((h) => (h.pos > 0 ? { ...h, pos: h.pos - 1 } : h)), []);
  const forward = useCallback(() => setHistory((h) => (h.pos < h.list.length - 1 ? { ...h, pos: h.pos + 1 } : h)), []);

  // ── time, dismissals, live events ────────────────────────────────────────
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);
  const { dismissed, dismiss } = useDismissed();

  const activitySettings = useMemo(() => activitySettingsFrom(ws.settings.activity), [ws.settings.activity]);
  const setActivitySettings = (next: ActivitySettings) => ws.updateSettings({ activity: next });
  const sound = ws.settings.sound !== false;
  const colourMode = readColourMode(ws.settings.colourMode);
  const colourOf = useCallback((accountId: string) => accountColour(accountId, colourMode), [colourMode]);
  const activeWindow: ActiveWindow = (ws.settings.activeWindow as ActiveWindow) || 'today';

  const [liveTick, setLiveTick] = useState(0);
  const { active, loaded: activeLoaded } = useActiveChats(ws, activeWindow);
  const openRef = useRef(open);
  openRef.current = open;
  const settingsRef = useRef({ activitySettings, sound });
  settingsRef.current = { activitySettings, sound };
  useEffect(
    () =>
      ws.onEvent((ev: WorkspaceEvent) => {
        const cur = openRef.current;
        if (cur && cur.accountId === ev.account_id && cur.fanId === ev.fan_id && LIVE_KINDS.has(ev.kind)) setLiveTick((n) => n + 1);
        if (settingsRef.current.sound && ev.kind !== 'team_sent' && ev.kind !== 'ai_sent' && ev.kind !== 'subscription' && activityItems([ev], settingsRef.current.activitySettings, {}, Date.now()).length) blip();
      }),
    [ws],
  );

  // ── pins ─────────────────────────────────────────────────────────────────
  // One board shared with every Account's Chats page (ADR 0035): pins and
  // unpins go to the server one chat at a time; a drag reorders the board.
  const { pins, loaded: pinsLoaded } = usePins(user?.id, ws.enabled);
  useEffect(() => () => pinStore.flush(), []);
  const isPinned = (accountId: string, fanId: string) => pins.some((p) => p.account_id === accountId && p.fan_id === fanId);
  const togglePin = (c: OpenChat | null) => {
    if (!c) return;
    if (isPinned(c.accountId, c.fanId)) void pinStore.remove(c.accountId, c.fanId);
    else void pinStore.add({ account_id: c.accountId, fan_id: c.fanId, fan_name: c.fanName ?? null, fan_username: c.fanUsername ?? null, fan_avatar: c.fanAvatar ?? null });
  };
  const unpin = (accountId: string, fanId: string) => void pinStore.remove(accountId, fanId);

  // ── feed ─────────────────────────────────────────────────────────────────
  const [menu, setMenu] = useState<Menu>(null);
  const feedSettings = useMemo(() => feedSettingsFrom(ws.settings.feed), [ws.settings.feed]);
  const setFeedSettings = (next: FeedSettings) => ws.updateSettings({ feed: next });
  const [feedHover, setFeedHover] = useState(false);
  // Saved settings first: a page built on the defaults would be thrown away a moment later.
  const feedOn = ws.enabled && ws.prefsLoaded && (view === 'tabs' || !board.hidden.feed);
  const feed = useFeed({
    enabled: feedOn,
    settings: feedSettings,
    scope: ws.settings.scope ?? 'all',
    onEvent: ws.onEvent,
    // Paused while hovered, while editing the board, while collapsed, and while its settings are open.
    autoRun: feedOn && view === 'canvas' && board.views.feed === 'card' && !feedHover && !board.editing && !board.collapsed.feed && menu !== 'feedSettings',
    openKey,
  });

  // ── cards ────────────────────────────────────────────────────────────────
  // Scope narrows Activity and Active chats at once (events already received
  // for an Account that left the scope are hidden); Pinned is explicit and stays.
  const scopeSetting = ws.settings.scope ?? 'all';
  const inScope = useMemo(() => {
    if (scopeSetting === 'all') return null;
    const ids = new Set(scopeSetting.filter((id) => accountsById.has(id)));
    return ids.size ? ids : null;
  }, [scopeSetting, accountsById]);
  const scopedEvents = useMemo(() => (inScope ? live.events.filter((e) => inScope.has(e.account_id)) : live.events), [live.events, inScope]);
  const scopedActive = useMemo(() => (inScope ? active.filter((c) => inScope.has(c.account_id)) : active), [active, inScope]);
  const liveActivity = useMemo(() => activityItems(scopedEvents, activitySettings, dismissed, now), [scopedEvents, activitySettings, dismissed, now]);
  // While the pointer is over Activity the list holds still; new items wait
  // behind a "N new" pill (shown on click or when the pointer leaves).
  const [frozen, setFrozen] = useState<ActivityItem[] | null>(null);
  const activity = useMemo(() => (frozen ? frozen.filter((i) => !((dismissed[i.id] ?? 0) > Date.now())) : liveActivity), [frozen, liveActivity, dismissed]);
  const newActivity = useMemo(() => {
    if (!frozen) return 0;
    const shown = new Set(frozen.map((i) => i.id));
    return liveActivity.filter((i) => !shown.has(i.id)).length;
  }, [frozen, liveActivity]);
  const liveActivityRef = useRef(liveActivity);
  liveActivityRef.current = liveActivity;
  const freezeActivity = useCallback(() => setFrozen(liveActivityRef.current), []);
  const thawActivity = useCallback(() => setFrozen(null), []);
  // Chats shown without a name or avatar (state created by a send, events
  // without the fan's profile) get both from OnlyFans, once per chat.
  const [fans, setFans] = useState<Map<string, FanInfo>>(new Map());
  const askedFans = useRef(new Set<string>());
  useEffect(() => {
    const missing = new Map<string, string[]>();
    const shown = [
      ...scopedActive,
      ...pins,
      ...feed.items.slice(0, 30),
      ...activity.slice(0, 40).map((a) => ({ account_id: a.event.account_id, fan_id: a.event.fan_id, fan_name: a.event.payload.fan_name, fan_avatar: a.event.payload.fan_avatar })),
    ];
    for (const c of shown) {
      const key = chatKey(c.account_id, c.fan_id);
      if ((c.fan_name && c.fan_avatar) || askedFans.current.has(key)) continue;
      askedFans.current.add(key);
      missing.set(c.account_id, [...(missing.get(c.account_id) || []), c.fan_id]);
    }
    for (const [accountId, fanIds] of missing) {
      fetchFanInfo(accountId, fanIds.slice(0, 50))
        .then((found) =>
          setFans((prev) => {
            const next = new Map(prev);
            for (const [fanId, info] of found) next.set(chatKey(accountId, fanId), info);
            return next;
          }),
        )
        .catch(() => undefined);
    }
  }, [scopedActive, pins, feed.items, activity]);
  // Card lists are memoized so a tick only re-draws the widgets whose cards changed.
  const cardCtx = useMemo<CardContext>(() => ({ accounts: accountsById, nowMs: now, t, tc, colourMode, fans }), [accountsById, now, t, tc, colourMode, fans]);
  const dismissOne = useCallback((id: string) => dismiss([id]), [dismiss]);
  const activityCardsList = useMemo(
    () => activity.slice(0, ACTIVITY_MAX_ROWS).map((item, i) => activityCard(item, i, cardCtx, dismissOne)),
    [activity, cardCtx, dismissOne],
  );
  const groups = useMemo(() => activeChatGroups(scopedActive, { window: activeWindow, nowMs: now }), [scopedActive, activeWindow, now]);
  const activeCardsList = useMemo(() => activeCards(groups, cardCtx), [groups, cardCtx]);
  const repliedCardsList = useMemo(() => activeCards({ replied: groups.replied, waiting: [] }, cardCtx), [groups.replied, cardCtx]);
  const skipFeed = useStableCallback((it: FeedItem) => feed.skip(it));
  const feedCardsList = useMemo(() => feedCards(feed.items, feedSettings, cardCtx, skipFeed), [feed.items, feedSettings, cardCtx, skipFeed]);
  // Pinned only re-draws when one of its own chats changes, not on every chat state.
  const pinStates = useStableArray(pins.map((p) => live.chatStates.get(chatKey(p.account_id, p.fan_id))));
  const unpinRow = useStableCallback((p: PinRow) => unpin(p.account_id, p.fan_id));
  const pinCardsList = useMemo(() => {
    const states = new Map<string, ChatStateRow>();
    pinStates.forEach((row) => row && states.set(chatKey(row.account_id, row.fan_id), row));
    return pinCards(pins, states, cardCtx, unpinRow);
  }, [pins, pinStates, cardCtx, unpinRow]);
  const selectedKeyFor = (prefix: string) => (openKey ? `${prefix}:${openKey}` : null);

  const openCard = (c: CardItem) => {
    openChat({ accountId: c.accountId, fanId: c.fanId, fanName: c.fanName, fanUsername: c.fanUsername, fanAvatar: c.fanAvatar });
    setFocus({ widget: c.key.startsWith('a:') ? 'activity' : c.key.startsWith('c:') ? 'active' : c.key.startsWith('f:') ? 'feed' : 'pinned', key: c.key });
  };
  // Drag a chat from anywhere onto Pinned to pin it (onto a pinned card to
  // put it before that one); drag a pinned card out of Pinned to unpin it.
  const onChatDrop = (chat: DragChat, source: DragSource, target: DropTarget) => {
    const same = (p: { account_id: string; fan_id: string }) => p.account_id === chat.account_id && p.fan_id === chat.fan_id;
    if (!target.pinned) {
      if (source === 'pinned') unpin(chat.account_id, chat.fan_id);
      return;
    }
    const rest = pins.filter((p) => !same(p));
    const known = pins.find(same);
    const row: PinRow = known ?? { ...chat };
    const at = target.beforeKey ? rest.findIndex((p) => `p:${chatKey(p.account_id, p.fan_id)}` === target.beforeKey) : -1;
    const order = at >= 0 ? [...rest.slice(0, at), row, ...rest.slice(at)] : [...rest, row];
    // A new pin goes to the server first, then takes its place in the order.
    if (known) pinStore.reorder(order);
    else void pinStore.add(row).then((res) => res === 'pinned' && at >= 0 && pinStore.reorder(order));
  };
  const { drag: chatDrag, start: startChatDrag } = useChatDrag(onChatDrop, !board.editing);
  const openCardStable = useStableCallback((c: CardItem) => openCard(c));
  const pinOver = !!chatDrag?.over.pinned;
  const pinDrag = chatDrag?.source === 'pinned';
  const cardChat = (c: CardItem): DragChat => ({ account_id: c.accountId, fan_id: c.fanId, fan_name: c.fanName, fan_username: c.fanUsername ?? null, fan_avatar: c.fanAvatar ?? null });
  const onCardDrag = useStableCallback((c: CardItem, e: PointerEvent) => startChatDrag(cardChat(c), 'other', e, c.key));
  const onPinnedDrag = useStableCallback((c: CardItem, e: PointerEvent) => startChatDrag(cardChat(c), 'pinned', e, c.key));

  // ── card view index + keyboard ───────────────────────────────────────────
  const [cardIdx, setCardIdx] = useState<Record<string, number>>({});
  const stepPinned = useCallback((d: number) => setCardIdx((c) => ({ ...c, pinned: (c.pinned || 0) + d })), []);
  const stepActivity = useCallback((d: number) => setCardIdx((c) => ({ ...c, activity: (c.activity || 0) + d })), []);
  const stepActive = useCallback((d: number) => setCardIdx((c) => ({ ...c, active: (c.active || 0) + d })), []);
  const stepFeed = useStableCallback((d: number) => (d > 0 ? feed.next() : feed.previous()));
  const feedRetry = useStableCallback(() => feed.retry());
  const feedBringBack = useStableCallback(() => void feed.bringBack());
  const [focus, setFocus] = useState<{ widget: ListWidgetId; key: string | null }>({ widget: 'activity', key: null });
  const searchRef = useRef<HTMLInputElement>(null);
  const [filtersAt, setFiltersAt] = useState<{ top: number; left: number } | null>(null);
  const tabCards = useMemo<Record<WorkspaceTab, CardItem[]>>(
    () => ({ activity: activityCardsList, suggestions: feedCardsList, active: activeCardsList, replied: repliedCardsList, pinned: pinCardsList }),
    [activityCardsList, feedCardsList, activeCardsList, repliedCardsList, pinCardsList],
  );
  const lists = { activity: activityCardsList, active: activeCardsList, pinned: pinCardsList, feed: feedCardsList };
  const keyState = useRef({ board, menu, open, focus, lists, view, tabList: tabCards[tab] });
  keyState.current = { board, menu, open, focus, lists, view, tabList: tabCards[tab] };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const s = keyState.current;
      const typing = /input|textarea|select/i.test((e.target as HTMLElement).tagName) || (e.target as HTMLElement).isContentEditable;
      if (e.key === 'Escape') {
        if (s.menu) setMenu(null);
        else if (s.board.editing) dispatch({ type: 'cancelEdit' }, false);
        return;
      }
      if (e.altKey && e.key === 'ArrowLeft') {
        e.preventDefault();
        back();
        return;
      }
      if (e.altKey && e.key === 'ArrowRight') {
        e.preventDefault();
        forward();
        return;
      }
      if (typing || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === '/') {
        e.preventDefault();
        if (s.view === 'tabs') setSearchRequest((n) => n + 1);
        else searchRef.current?.focus();
      } else if (e.key === 'Enter' && s.open) {
        e.preventDefault();
        document.querySelector<HTMLTextAreaElement>('.ws-pane .rc-composer textarea')?.focus();
      } else if (e.key === 'p' || e.key === 'P') {
        togglePinRef.current(s.open);
      } else if (e.key === 'j' || e.key === 'J' || e.key === 'k' || e.key === 'K') {
        const list = (s.view === 'tabs' ? s.tabList : s.lists[s.focus.widget]) || [];
        if (!list.length) return;
        const i = list.findIndex((c) => c.key === s.focus.key);
        const next = list[Math.max(0, Math.min(list.length - 1, i + (e.key.toLowerCase() === 'j' ? 1 : -1)))] ?? list[0]!;
        openCardRef.current(next);
      } else if ((e.key === 'x' || e.key === 'X') && s.focus.widget === 'activity' && s.focus.key) {
        dismiss([s.focus.key.slice(2)]);
      } else if ((e.key === 'x' || e.key === 'X') && s.focus.widget === 'feed') {
        const card = s.lists.feed.find((c) => c.key === s.focus.key) ?? s.lists.feed[0];
        card?.action?.run();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [back, forward, dismiss, dispatch]);
  // Stable props for the (memoized) chat pane: a tick elsewhere must not re-draw the open chat.
  const openColour = useMemo(() => colourOf(open?.accountId ?? ''), [colourOf, open?.accountId]);
  const togglePinOpen = useStableCallback(() => togglePin(openRef.current));
  const dragOpenFan = useStableCallback((e: PointerEvent, fan: { name: string | null; username: string | null; avatar: string | null }) => {
    const cur = openRef.current;
    if (cur) startChatDrag({ account_id: cur.accountId, fan_id: cur.fanId, fan_name: fan.name, fan_username: fan.username, fan_avatar: fan.avatar }, 'other', e);
  });
  const togglePinRef = useRef(togglePin);
  togglePinRef.current = togglePin;
  const openCardRef = useRef(openCard);
  openCardRef.current = openCard;

  // ── scope + search account ───────────────────────────────────────────────
  const scope = scopeSetting;
  const scopeAccounts: CrmAccount[] = scope === 'all' ? ws.accounts : ws.accounts.filter((a) => scope.includes(a.account_id));
  const shownScope = scopeAccounts.length ? scopeAccounts : ws.accounts;
  const searchAccountId = (ws.settings.searchAccount as string) && accountsById.has(ws.settings.searchAccount as string) ? (ws.settings.searchAccount as string) : shownScope[0]?.account_id ?? null;
  // The scope menu edits a draft; nothing is saved (or re-streamed) until Save.
  const [scopeDraft, setScopeDraft] = useState<string[]>([]);
  const allIds = ws.accounts.map((a) => a.account_id);
  const openScopeMenu = () => {
    setScopeDraft(scope === 'all' ? allIds : allIds.filter((id) => scope.includes(id)));
    setMenu('scope');
  };
  const toggleScopeDraft = (id: string) => setScopeDraft((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const savedScopeIds = scope === 'all' ? allIds : allIds.filter((id) => scope.includes(id));
  const scopeDirty = scopeDraft.length !== savedScopeIds.length || scopeDraft.some((id) => !savedScopeIds.includes(id));
  const saveScope = () => {
    if (!scopeDraft.length) return;
    ws.updateSettings({ scope: scopeDraft.length === allIds.length ? 'all' : allIds.filter((id) => scopeDraft.includes(id)) });
    setMenu(null);
  };

  // ── tabs view ────────────────────────────────────────────────────────────
  const openFloating = (which: 'filters' | 'feedSettings', width: number) => (e: ReactMouseEvent<HTMLButtonElement>) => {
    if (menu === which) return setMenu(null);
    const r = e.currentTarget.getBoundingClientRect();
    setFiltersAt(floatingAt(r, width));
    setMenu(which);
  };
  const windowPicker = (
    <Dropdown
      className="ws-dd--window"
      label={
        <>
          <FeatherIcon name="clock" size={11} />
          {t(`active.window.${activeWindow}`)}
        </>
      }
    >
      {(_q, close) =>
        ACTIVE_WINDOWS.map((w) => (
          <button key={w} type="button" role="option" aria-selected={w === activeWindow} className={`ws-menu-item ${w === activeWindow ? 'is-on' : ''}`} onClick={() => (ws.updateSettings({ activeWindow: w }), close())}>
            <span className="ws-check">{w === activeWindow && <FeatherIcon name="check" size={10} />}</span>
            <span className="ws-menu-label">{t(`active.window.${w}`)}</span>
          </button>
        ))
      }
    </Dropdown>
  );
  const tabTools =
    tab === 'activity' ? (
      <>
        <button type="button" className="ws-icon-btn" title={t('activity.clearAll')} onClick={() => dismiss([...new Set([...activity, ...liveActivity].map((a) => a.id))])}>
          <FeatherIcon name="list-x" size={14} />
        </button>
        <button type="button" className={`ws-icon-btn ${menu === 'filters' ? 'is-on' : ''}`} title={t('activity.filters.title')} onClick={openFloating('filters', 300)}>
          <FeatherIcon name="settings-2" size={14} />
        </button>
      </>
    ) : tab === 'suggestions' ? (
      <>
        <button type="button" className="ws-icon-btn" title={t('feed.reload')} disabled={feed.loading} onClick={feed.retry}>
          {feed.loading ? <Spinner size={14} label={t('page.loading')} /> : <FeatherIcon name="refresh-cw" size={14} />}
        </button>
        <button type="button" className={`ws-icon-btn ${menu === 'feedSettings' ? 'is-on' : ''}`} title={t('feed.settings.title')} onClick={openFloating('feedSettings', 320)}>
          <FeatherIcon name="settings-2" size={14} />
        </button>
      </>
    ) : tab === 'active' || tab === 'replied' ? (
      windowPicker
    ) : null;
  const tabLoading: Record<WorkspaceTab, boolean> = {
    activity: !live.events.length && ws.status === 'connecting',
    // While more is on its way (the rest of the first page, a refill) the list shows a spinner under its rows.
    suggestions: !feed.loaded || feed.loading || feed.lists.some((l) => l.status === 'syncing' || l.status === 'refreshing'),
    active: !activeLoaded,
    replied: !activeLoaded,
    pinned: !pinsLoaded,
  };
  const feedState = { error: !!feed.error, retry: feedRetry, bringBack: feedBringBack };
  const activityState = { newCount: newActivity, freeze: freezeActivity, thaw: thawActivity, reconnecting: ws.status === 'reconnecting' };
  const openHit = useStableCallback((h: SearchHit) => openChat({ accountId: h.account_id, fanId: h.fan_id, fanName: h.fan_name, fanUsername: h.fan_username, fanAvatar: h.fan_avatar }));
  const startTabsDrag = useStableCallback((c: Parameters<TabsDrag['start']>[0], fromPinned: boolean, e: PointerEvent, key: string) =>
    startChatDrag({ account_id: c.accountId, fan_id: c.fanId, fan_name: c.fanName, fan_username: c.fanUsername ?? null, fan_avatar: c.fanAvatar ?? null }, fromPinned ? 'pinned' : 'other', e, key),
  );
  const tabsDrag: TabsDrag = { start: startTabsDrag, draggingKey: chatDrag?.key ?? null, active: !!chatDrag, overPinned: pinOver };
  const tabsSearch = {
    accounts: shownScope,
    accountId: searchAccountId,
    onAccount: (id: string) => ws.updateSettings({ searchAccount: id }),
    accountName: (id: string) => {
      const a = accountsById.get(id);
      return a?.profile_name || a?.username || '';
    },
    request: searchRequest,
  };

  // ── rendering ────────────────────────────────────────────────────────────
  const frameFor = (id: WidgetId): FrameState => {
    const cell = board.layout[id];
    const rows = board.collapsed[id] && id !== 'search' ? 2 : cell.h;
    let rect = cellRect(cell, rows, geo);
    if (drag?.id === id) {
      const o = cellRect(drag.orig, rows, geo);
      rect = { ...o, left: o.left + drag.dx, top: o.top + drag.dy };
    }
    return { rect, dragging: drag?.id === id, resizing: resizing === id, animate: animate && dividerDrag == null, editing: board.editing, collapsed: !!board.collapsed[id] && id !== 'search' };
  };
  const visible = WIDGET_IDS.filter((id) => !board.hidden[id]);
  const maxY = Math.max(0, ...visible.map((id) => board.layout[id].y + (board.collapsed[id] && id !== 'search' ? 2 : board.layout[id].h)));
  const innerH = Math.max(geo.pad * 2 + maxY * (geo.rowH + geo.gap) - geo.gap + (drag || resizing ? geo.rowH * 8 : 0), size.zh);
  const ghost = drag || resizing ? cellRect(board.layout[(drag?.id ?? resizing)!], board.layout[(drag?.id ?? resizing)!].h, geo) : null;
  const addable = addableWidgets(board);
  const widgetName = (id: WidgetId) => t(`widgets.${id}`);

  if (!ws.enabled) {
    return (
      <div className="ws-page ws-page--empty">
        {crm.access.state === 'loading' ? (
          <LoadingBlock label={t('page.loading')} />
        ) : (
          <>
            <FeatherIcon name="layout-dashboard" size={26} />
            <b>{t('page.noAccessTitle')}</b>
            <span>{t('page.noAccessText')}</span>
          </>
        )}
      </div>
    );
  }

  const openAccount = open ? accountsById.get(open.accountId) ?? null : null;
  const openState = openKey ? live.chatStates.get(openKey) : undefined;
  const openAi =
    openState?.ai_status ?? active.find((c) => openKey === chatKey(c.account_id, c.fan_id))?.ai_status ?? pins.find((p) => openKey === chatKey(p.account_id, p.fan_id))?.ai_status ?? null;

  const addMenuItems = (
    <>
      {addable.map((id) => (
        <button key={id} type="button" className="ws-menu-item" onClick={() => (dispatch({ type: 'add', id }, !board.editing), setMenu(null))}>
          <FeatherIcon name={WIDGET_ICON[id]} size={14} />
          <span className="ws-menu-label">{widgetName(id)}</span>
          <FeatherIcon name="plus" size={13} />
        </button>
      ))}
    </>
  );

  return (
    <div className={`ws-page ${chatDrag ? 'is-chat-drag' : ''} ${phone ? `is-phone ${phoneChat && open ? 'is-phone-chat' : ''}` : ''}`}>
      {/* Phones: an open chat takes the whole screen, the shell's tab bar included. */}
      {phone && phoneChat && open && <ClaimBottomBar />}
      <div className={`ws-topbar ${board.editing ? 'is-editing' : ''}`}>
        <h1 className="ws-title">{t('page.title')}</h1>
        {!board.editing ? (
          <>
            <div className="ws-rel">
              <button type="button" className="ws-scope" onClick={() => (menu === 'scope' ? setMenu(null) : openScopeMenu())}>
                <span className="ws-scope-avs">
                  {shownScope.slice(0, 4).map((a) => (
                    <Avatar key={a.account_id} src={a.avatar_url} name={a.profile_name || a.username} size={20} ring={`0 0 0 2px var(--om-panel), 0 0 0 3px ${colourOf(a.account_id).accent}`} />
                  ))}
                </span>
                <span>{scope === 'all' ? t('scope.all') : t('scope.some')}</span>
                <span className="ws-scope-count">{t('scope.count', { count: ws.scopeIds.length || shownScope.length })}</span>
                <FeatherIcon name="chevron-down" size={14} />
              </button>
              {menu === 'scope' && (
                <div className="ws-menu ws-scope-menu">
                  <div className="ws-menu-actions">
                    <span className="ws-menu-label ws-muted">{t('scope.selected', { count: scopeDraft.length, total: allIds.length })}</span>
                    <button type="button" className="ws-link-btn" disabled={scopeDraft.length === allIds.length} onClick={() => setScopeDraft(allIds)}>
                      {t('scope.selectAll')}
                    </button>
                    <button type="button" className="ws-link-btn" disabled={!scopeDraft.length} onClick={() => setScopeDraft([])}>
                      {t('scope.clearAll')}
                    </button>
                  </div>
                  <span className="ws-menu-sep" />
                  <div className="ws-menu-scroll">
                    {ws.accounts.map((a) => {
                      const on = scopeDraft.includes(a.account_id);
                      return (
                        <button key={a.account_id} type="button" className="ws-menu-item" onClick={() => toggleScopeDraft(a.account_id)}>
                          <span className="ws-check">{on && <FeatherIcon name="check" size={10} />}</span>
                          <Avatar src={a.avatar_url} name={a.profile_name || a.username} size={20} ring={`0 0 0 1.5px ${colourOf(a.account_id).accent}`} />
                          <span className="ws-menu-label">{a.profile_name || a.username}</span>
                          <span className="ws-muted">@{a.username}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="ws-menu-foot">
                    {!scopeDraft.length && <span className="ws-menu-label ws-muted">{t('scope.pickOne')}</span>}
                    <button type="button" className="ws-btn ws-btn--ghost" onClick={() => setMenu(null)}>
                      {t('layout.cancel')}
                    </button>
                    <button type="button" className="ws-btn ws-btn--primary" disabled={!scopeDraft.length || !scopeDirty} onClick={saveScope}>
                      {t('scope.save')}
                    </button>
                  </div>
                </div>
              )}
            </div>
            <span className={`ws-live ws-live--${ws.status}`}>
              <span />
              {t(`live.${ws.status}`)}
            </span>
            <span className="ws-flex" />
            <button type="button" className="ws-icon-btn ws-icon-btn--32" title={sound ? t('page.soundOn') : t('page.soundOff')} onClick={() => ws.updateSettings({ sound: !sound })}>
              <FeatherIcon name={sound ? 'volume-2' : 'volume-x'} size={16} />
            </button>
            <div className="ws-rel">
              <button type="button" className={`ws-btn ${menu === 'layout' ? 'is-on' : ''}`} onClick={() => setMenu(menu === 'layout' ? null : 'layout')}>
                <FeatherIcon name="layout-dashboard" size={14} className="ws-accent-ic" />
                {t('layout.button')}
                <FeatherIcon name="chevron-down" size={13} />
              </button>
              {menu === 'layout' && (
                <div className="ws-menu ws-menu--right ws-layout-menu">
                  {!phone && (
                    <>
                      <span className="ws-menu-section">{t('layout.view')}</span>
                      {(['tabs', 'canvas'] as const).map((v) => (
                        <button key={v} type="button" className={`ws-menu-item ${view === v ? 'is-on' : ''}`} onClick={() => (ws.updateSettings({ view: v }), setMenu(null))}>
                          <span className="ws-check">{view === v && <FeatherIcon name="check" size={10} />}</span>
                          <FeatherIcon name={v === 'tabs' ? 'list' : 'layout-dashboard'} size={14} />
                          <span className="ws-menu-label">{t(`layout.views.${v}`)}</span>
                          <span className="ws-muted">{t(`layout.views.${v}Hint`)}</span>
                        </button>
                      ))}
                      <span className="ws-menu-sep" />
                    </>
                  )}
                  {view === 'canvas' && (
                    <>
                      <button type="button" className="ws-menu-item" onClick={() => (dispatch({ type: 'enterEdit' }, false), setMenu(null))}>
                        <FeatherIcon name="pencil" size={14} className="ws-accent-ic" />
                        <span className="ws-menu-label">{t('layout.edit')}</span>
                        <span className="ws-muted">{t('layout.editHint')}</span>
                      </button>
                      <span className="ws-menu-sep" />
                      <span className="ws-menu-section">{t('layout.addWidget')}</span>
                      {addMenuItems}
                      <span className="ws-menu-sep" />
                    </>
                  )}
                  <span className="ws-menu-section">{t('layout.colours')}</span>
                  {COLOUR_MODES.map((m) => (
                    <button key={m} type="button" className={`ws-menu-item ${colourMode === m ? 'is-on' : ''}`} onClick={() => ws.updateSettings({ colourMode: m })}>
                      <span className="ws-check">{colourMode === m && <FeatherIcon name="check" size={10} />}</span>
                      <span className="ws-menu-label">{t(`layout.colourMode.${m}`)}</span>
                    </button>
                  ))}
                  {view === 'canvas' && (
                    <>
                      <span className="ws-menu-sep" />
                      <button type="button" className="ws-menu-item" onClick={() => (dispatch({ type: 'reset', geometry: geo }), setMenu(null))}>
                        <FeatherIcon name="rotate-ccw" size={14} />
                        <span className="ws-menu-label">{t('layout.reset')}</span>
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <span className="ws-editing-pill">
              <FeatherIcon name="pencil" size={12} />
              {t('layout.editing')}
            </span>
            <span className="ws-edit-hint">{t('layout.editingHint')}</span>
            <span className="ws-flex" />
            <div className="ws-rel">
              <button type="button" className={`ws-btn ${menu === 'add' ? 'is-on' : ''}`} onClick={() => setMenu(menu === 'add' ? null : 'add')}>
                <FeatherIcon name="plus" size={14} className="ws-accent-ic" />
                {t('layout.addWidget')}
                <span className="ws-count">{addable.length}</span>
              </button>
              {menu === 'add' && <div className="ws-menu ws-menu--right ws-layout-menu">{addMenuItems}</div>}
            </div>
            <button type="button" className="ws-btn ws-btn--ghost" onClick={() => dispatch({ type: 'reset', geometry: geo }, false)}>
              <FeatherIcon name="rotate-ccw" size={14} />
              {t('layout.resetShort')}
            </button>
            <button type="button" className="ws-btn" onClick={() => dispatch({ type: 'cancelEdit' }, false)}>
              {t('layout.cancel')}
            </button>
            <button type="button" className="ws-btn ws-btn--primary" onClick={() => dispatch({ type: 'doneEdit' })}>
              {t('layout.done')}
            </button>
          </>
        )}
      </div>

      <div className="ws-row-split" ref={rowRef}>
        {view === 'tabs' && (
          <div className="ws-tabs-zone cp-page cp-embed" style={phone ? undefined : { width: tabsW }}>
            <TabsView
              tab={tab}
              onTab={setTab}
              cards={tabCards}
              loading={tabLoading}
              repliedCount={groups.replied.length}
              tools={tabTools}
              feed={feedState}
              activity={activityState}
              singleAccount={shownScope.length === 1}
              colourOf={colourOf}
              openKey={openKey}
              onOpen={openCardStable}
              onOpenHit={openHit}
              drag={tabsDrag}
              search={tabsSearch}
            />
          </div>
        )}
        {view === 'canvas' && (
        <div className={`ws-zone ws-scroll ${board.editing ? 'is-editing' : ''}`} ref={zoneRef} style={{ width: leftW }}>
          <div className="ws-zone-inner" style={{ height: innerH }}>
            {board.editing &&
              Array.from({ length: 12 }, (_, i) => <span key={i} className="ws-guide" style={{ left: geo.pad + i * (geo.colW + geo.gap), width: geo.colW }} />)}
            {ghost && <span className="ws-ghost" style={ghost} />}

            {!board.hidden.search && (
              <SearchWidget
                frame={frameFor('search')}
                accounts={shownScope}
                inputRef={searchRef}
                accountId={searchAccountId}
                colourOf={colourOf}
                onAccount={(id) => ws.updateSettings({ searchAccount: id })}
                onPick={(h) => openChat({ accountId: h.account_id, fanId: h.fan_id, fanName: h.fan_name, fanUsername: h.fan_username, fanAvatar: h.fan_avatar })}
                onDragStart={(h, e) => startChatDrag({ account_id: h.account_id, fan_id: h.fan_id, fan_name: h.fan_name, fan_username: h.fan_username || null, fan_avatar: h.fan_avatar }, 'other', e)}
                onRemove={() => dispatch({ type: 'remove', id: 'search' }, !board.editing)}
                onMoveStart={startMove('search')}
                onResizeStart={startResize('search')}
              />
            )}

            {!board.hidden.pinned && (
              <WidgetFrame
                frame={frameFor('pinned')}
                icon="pin"
                title={widgetName('pinned')}
                count={pins.length}
                view={board.views.pinned}
                onView={(v) => dispatch({ type: 'setView', id: 'pinned', view: v }, !board.editing)}
                onToggleCollapse={() => dispatch({ type: 'toggleCollapse', id: 'pinned' }, !board.editing)}
                onRemove={() => dispatch({ type: 'remove', id: 'pinned' }, !board.editing)}
                onMoveStart={startMove('pinned')}
                onResizeStart={startResize('pinned')}
                className={chatDrag ? (pinOver ? 'is-drop' : pinDrag ? '' : 'is-drop-ready') : ''}
                dropZone="pinned"
              >
                <ChatCollection
                  items={pinCardsList}
                  view={board.views.pinned}
                  selectedKey={selectedKeyFor('p')}
                  focusKey={focus.widget === 'pinned' ? focus.key : null}
                  cardIndex={cardIdx.pinned || 0}
                  onCardIndex={stepPinned}
                  onOpen={openCardStable}
                  onDragStart={onPinnedDrag}
                  draggingKey={chatDrag?.key ?? null}
                  drop={{
                    over: pinOver,
                    text: pinDrag ? (pinOver ? t('pinned.dropReorder') : t('pinned.dragOut')) : pinOver ? t('pinned.drop') : pinCardsList.length ? t('pinned.dragHere') : t('pinned.emptyHint'),
                  }}
                  empty={{ icon: 'pin', title: t('pinned.emptyTitle'), text: t('pinned.emptyHint'), loading: !pinsLoaded }}
                />
              </WidgetFrame>
            )}

            {!board.hidden.activity && (
              <WidgetFrame
                frame={frameFor('activity')}
                icon="activity"
                title={widgetName('activity')}
                count={activity.length}
                view={board.views.activity}
                onView={(v) => dispatch({ type: 'setView', id: 'activity', view: v }, !board.editing)}
                onToggleCollapse={() => dispatch({ type: 'toggleCollapse', id: 'activity' }, !board.editing)}
                onRemove={() => dispatch({ type: 'remove', id: 'activity' }, !board.editing)}
                onMoveStart={startMove('activity')}
                onResizeStart={startResize('activity')}
                tools={
                  <>
                    <button type="button" className="ws-icon-btn" title={t('activity.clearAll')} onClick={() => dismiss([...new Set([...activity, ...liveActivity].map((a) => a.id))])}>
                      <FeatherIcon name="list-x" size={14} />
                    </button>
                    <button
                      type="button"
                      className={`ws-icon-btn ${menu === 'filters' ? 'is-on' : ''}`}
                      title={t('activity.filters.title')}
                      onClick={(e) => {
                        if (menu === 'filters') return setMenu(null);
                        const r = e.currentTarget.getBoundingClientRect();
                        setFiltersAt(floatingAt(r, 300));
                        setMenu('filters');
                      }}
                    >
                      <FeatherIcon name="settings-2" size={14} />
                    </button>
                  </>
                }
                banner={
                  ws.status === 'reconnecting' ? (
                    <div className="ws-banner">
                      <FeatherIcon name="wifi-off" size={13} />
                      {t('live.reconnectingBanner')}
                    </div>
                  ) : null
                }
              >
                <div className="ws-feed-body" onMouseEnter={freezeActivity} onMouseLeave={thawActivity}>
                  {newActivity > 0 && (
                    <button type="button" className="ws-new-pill" title={t('activity.pausedHint')} onClick={freezeActivity}>
                      <FeatherIcon name="arrow-up" size={12} />
                      {t('activity.newItems', { count: newActivity })}
                    </button>
                  )}
                  <ChatCollection
                    items={activityCardsList}
                    view={board.views.activity}
                    selectedKey={null}
                    focusKey={focus.widget === 'activity' ? focus.key : null}
                    cardIndex={cardIdx.activity || 0}
                    onCardIndex={stepActivity}
                    onOpen={openCardStable}
                    onDragStart={onCardDrag}
                    draggingKey={chatDrag?.key ?? null}
                    empty={{ icon: 'activity', title: t('activity.emptyTitle'), text: t('activity.emptyText'), loading: !live.events.length && ws.status === 'connecting' }}
                  />
                </div>
              </WidgetFrame>
            )}

            {!board.hidden.feed && (
              <WidgetFrame
                frame={frameFor('feed')}
                icon="sparkles"
                title={widgetName('feed')}
                count={feed.items.length}
                view={board.views.feed}
                onView={(v) => dispatch({ type: 'setView', id: 'feed', view: v }, !board.editing)}
                onToggleCollapse={() => dispatch({ type: 'toggleCollapse', id: 'feed' }, !board.editing)}
                onRemove={() => dispatch({ type: 'remove', id: 'feed' }, !board.editing)}
                onMoveStart={startMove('feed')}
                onResizeStart={startResize('feed')}
                tools={
                  <>
                    {board.views.feed === 'card' && feedSettings.autoAdvance > 0 && feed.items.length > 0 && (
                      <span className={`ws-auto-chip ${feed.running ? 'is-on' : ''}`} title={t('feed.autoHint')}>
                        <FeatherIcon name={feed.running ? 'play' : 'pause'} size={10} />
                        {feed.running ? t('feed.auto', { seconds: feedSettings.autoAdvance }) : t('feed.paused')}
                      </span>
                    )}
                    <button type="button" className="ws-icon-btn" title={t('feed.reload')} disabled={feed.loading} onClick={feed.retry}>
                      {feed.loading ? <Spinner size={14} label={t('page.loading')} /> : <FeatherIcon name="refresh-cw" size={14} />}
                    </button>
                    <button
                      type="button"
                      className={`ws-icon-btn ${menu === 'feedSettings' ? 'is-on' : ''}`}
                      title={t('feed.settings.title')}
                      onClick={(e) => {
                        if (menu === 'feedSettings') return setMenu(null);
                        const r = e.currentTarget.getBoundingClientRect();
                        setFiltersAt(floatingAt(r, 320));
                        setMenu('feedSettings');
                      }}
                    >
                      <FeatherIcon name="settings-2" size={14} />
                    </button>
                  </>
                }
                banner={
                  board.views.feed === 'card' && feedSettings.autoAdvance > 0 && feed.items.length > 0 ? (
                    <div className="ws-progress">
                      {feed.running && <span key={feed.cycle} style={{ animationDuration: `${feedSettings.autoAdvance}s` }} />}
                    </div>
                  ) : null
                }
              >
                <div className="ws-feed-body" onMouseEnter={() => setFeedHover(true)} onMouseLeave={() => setFeedHover(false)}>
                  <ChatCollection
                    items={feedCardsList}
                    view={board.views.feed}
                    selectedKey={selectedKeyFor('f')}
                    focusKey={focus.widget === 'feed' ? focus.key : null}
                    cardIndex={0}
                    onCardIndex={stepFeed}
                    onOpen={openCardStable}
                    onDragStart={onCardDrag}
                  draggingKey={chatDrag?.key ?? null}
                    empty={
                      !feed.loaded || (feed.loading && !feed.items.length) || feed.lists.some((l) => l.status === 'syncing' || l.status === 'refreshing')
                        ? { icon: 'sparkles', title: t('page.loading'), text: '', loading: true }
                        : feed.error
                          ? {
                              icon: 'wifi-off',
                              title: t('feed.errorTitle'),
                              text: t('feed.errorText'),
                              action: (
                                <button type="button" className="ws-btn" onClick={feedRetry}>
                                  {t('feed.retry')}
                                </button>
                              ),
                            }
                          : {
                              icon: 'sparkles',
                              title: t('feed.emptyTitle'),
                              text: t('feed.emptyText'),
                              action: (
                                <button type="button" className="ws-btn" onClick={feedBringBack}>
                                  {t('feed.bringBack')}
                                </button>
                              ),
                            }
                    }
                  />
                </div>
              </WidgetFrame>
            )}

            {!board.hidden.active && (
              <WidgetFrame
                frame={frameFor('active')}
                icon="messages-square"
                title={widgetName('active')}
                count={groups.replied.length + groups.waiting.length}
                view={board.views.active}
                onView={(v) => dispatch({ type: 'setView', id: 'active', view: v }, !board.editing)}
                onToggleCollapse={() => dispatch({ type: 'toggleCollapse', id: 'active' }, !board.editing)}
                onRemove={() => dispatch({ type: 'remove', id: 'active' }, !board.editing)}
                onMoveStart={startMove('active')}
                onResizeStart={startResize('active')}
                tools={
                  <button
                    type="button"
                    className="ws-window-btn"
                    title={t('active.windowHint')}
                    onClick={() => ws.updateSettings({ activeWindow: ACTIVE_WINDOWS[(ACTIVE_WINDOWS.indexOf(activeWindow) + 1) % ACTIVE_WINDOWS.length] })}
                  >
                    <FeatherIcon name="clock" size={11} />
                    {t(`active.window.${activeWindow}`)}
                  </button>
                }
              >
                <ChatCollection
                  items={activeCardsList}
                  view={board.views.active}
                  selectedKey={selectedKeyFor('c')}
                  focusKey={focus.widget === 'active' ? focus.key : null}
                  cardIndex={cardIdx.active || 0}
                  onCardIndex={stepActive}
                  onOpen={openCardStable}
                  onDragStart={onCardDrag}
                  draggingKey={chatDrag?.key ?? null}
                  empty={{ icon: 'messages-square', title: activeLoaded ? t('active.emptyTitle') : t('page.loading'), text: activeLoaded ? t('active.emptyText') : '', loading: !activeLoaded }}
                />
              </WidgetFrame>
            )}

            {!visible.length && (
              <div className="ws-board-empty">
                <FeatherIcon name="layout-dashboard" size={24} />
                <b>{t('layout.emptyTitle')}</b>
                <span>{t('layout.emptyText')}</span>
              </div>
            )}
          </div>
        </div>
        )}

        <div className={`ws-divider ${dividerDrag != null ? 'is-active' : ''}`} onPointerDown={startDivider} title={t('page.dragToResize')}>
          <span className="ws-divider-line" />
          <span className="ws-divider-grip" />
        </div>

        <ChatPane
          chat={open}
          account={openAccount}
          colour={openColour}
          myUsername={ws.me?.username || ''}
          liveTick={liveTick}
          aiStatus={openAi}
          pinned={!!open && isPinned(open.accountId, open.fanId)}
          canBack={history.pos > 0}
          canForward={history.pos < history.list.length - 1}
          onBack={back}
          onForward={forward}
          onTogglePin={togglePinOpen}
          onList={phone ? toList : undefined}
          onFanDragStart={dragOpenFan}
          onOpenChat={openChat}
          crmUserId={user?.id ?? ws.me?.id ?? ''}
          compact={phone}
        />
      </div>
      {chatDrag && (
        <div className={`ws-drag-ghost ${pinDrag && !pinOver ? 'is-unpin' : ''} ${pinOver ? 'is-over' : ''}`} style={{ left: chatDrag.x + 14, top: chatDrag.y + 10 }}>
          <FanAvatar accountId={chatDrag.chat.account_id} src={chatDrag.chat.fan_avatar} name={chatDrag.chat.fan_name} size={20} ring={`0 0 0 1.5px ${colourOf(chatDrag.chat.account_id).accent}`} />
          <span className="ws-drag-ghost-name">{chatDrag.chat.fan_name || chatDrag.chat.fan_username || chatDrag.chat.fan_id}</span>
          <span className="ws-drag-ghost-hint">
            <FeatherIcon name={pinDrag && !pinOver ? 'pin-off' : 'pin'} size={11} />
            {pinDrag ? (pinOver ? t('pinned.ghostMove') : t('pinned.ghostUnpin')) : pinOver ? t('pinned.ghostPin') : t('pinned.ghostToPinned')}
          </span>
        </div>
      )}
      {menu && <div className="ws-menu-backdrop" onMouseDown={() => setMenu(null)} />}
      {menu === 'filters' && filtersAt && (
        <div className="ws-floating" style={{ top: filtersAt.top, left: filtersAt.left }}>
          <ActivityFilters value={activitySettings} accounts={shownScope} colourOf={colourOf} onChange={setActivitySettings} onClose={() => setMenu(null)} />
        </div>
      )}
      {menu === 'feedSettings' && filtersAt && (
        <div className="ws-floating" style={{ top: filtersAt.top, left: filtersAt.left }}>
          <FeedSettingsPanel value={feedSettings} accounts={shownScope} colourOf={colourOf} onChange={setFeedSettings} onClose={() => setMenu(null)} />
        </div>
      )}
    </div>
  );
}
